import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { usePostSendSms, usePostVerifySms } from "@/lib/api/sms/hooks";
import { usePostSignup } from "@/lib/api/auth/hooks";
import { useSignupStore } from "@/lib/store/use-signup-store";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { isAxiosError } from "axios";
import { phoneNumberRegex } from "@/lib/regex";

const ERROR_MESSAGE = {
  PHONE_NUMBER_REQUIRED: "휴대전화 번호를 입력해주세요.",
  PHONE_NUMBER_FORMAT:
    "휴대전화 번호는 010으로 시작하는 11자리 숫자여야 합니다.",
  VERIFICATION_CODE_REQUIRED: "인증번호를 입력해주세요.",
  VERIFICATION_CODE_FORMAT: "인증번호는 6자리 숫자여야 합니다.",
};

const VERIFICATION_TIME_LIMIT = 300; // 5분 (300초)

// Zod 스키마 정의
const smsVerificationSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, ERROR_MESSAGE.PHONE_NUMBER_REQUIRED)
    .regex(phoneNumberRegex, ERROR_MESSAGE.PHONE_NUMBER_FORMAT),
  verificationCode: z
    .string()
    .min(1, ERROR_MESSAGE.VERIFICATION_CODE_REQUIRED)
    .regex(/^[0-9]{6}$/, ERROR_MESSAGE.VERIFICATION_CODE_FORMAT)
    .optional(),
});

export type SmsVerificationFormData = z.infer<typeof smsVerificationSchema>;

export function useSmsVerification() {
  const navigate = useNavigate();
  const signupStore = useSignupStore();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(VERIFICATION_TIME_LIMIT);
  const [sentPhoneNumber, setSentPhoneNumber] = useState<string>("");
  const [cooldownTime, setCooldownTime] = useState(0);

  const {
    name,
    userId,
    birthDate,
    password,
    parentInputName,
    parentInputBirth,
    parentCertNum,
  } = signupStore;

  const {
    control,
    handleSubmit,
    formState: { isValid },
    trigger,
    getValues,
    setError,
    reset,
  } = useForm<SmsVerificationFormData>({
    resolver: zodResolver(smsVerificationSchema),
    mode: "onChange",
    defaultValues: {
      phoneNumber: "",
      verificationCode: "",
    },
  });

  // SMS 전송 mutation
  const { mutateAsync: sendSms, isPending: isSending } = usePostSendSms({
    onError: (error: Error) => {
      if (isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || "";

        // 429 에러 (일일 발송 제한 초과) 처리
        if (error.response?.status === 429) {
          setAlertDialogState({
            title: "발송 제한 초과",
            description:
              "일일 발송 횟수를 초과했습니다.\n관리자에게 문의해주세요.",
            confirmText: "확인",
            onConfirm: () => {
              resetAlertDialogState();
            },
          });
          return;
        }

        // 400 에러 중 쿨타임 에러 (XX초 후에 재전송) 처리
        if (
          error.response?.status === 400 &&
          errorMessage.includes("초 후에 재전송할 수 있습니다")
        ) {
          setAlertDialogState({
            title: "잠시만 기다려주세요",
            description: errorMessage,
            confirmText: "확인",
            onConfirm: () => {
              resetAlertDialogState();
            },
          });
          return;
        }
      }

      // 그 외 에러는 필드 에러로 표시
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message || "인증번호 전송에 실패했습니다.";
      setError("phoneNumber", { message });
    },
  });

  // SMS 인증 mutation
  const { mutateAsync: verifySms, isPending: isVerifying } = usePostVerifySms({
    onError: (error: Error) => {
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message || "인증번호 확인에 실패했습니다.";
      setError("verificationCode", { message });
    },
  });

  // 회원가입 mutation
  const { mutateAsync: signup, isPending: isSigningUp } = usePostSignup({
    onError: (error: Error) => {
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data
          ?.message || "회원가입에 실패했습니다.";
      setError("verificationCode", { message });
    },
  });

  useLoadingIndicator(isSending || isVerifying || isSigningUp);

  // 인증번호 유효시간 타이머 (5분)
  useEffect(() => {
    if (!isVerificationSent) return;

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVerificationSent]);

  // 재발송 쿨타임 타이머 (60초)
  useEffect(() => {
    if (cooldownTime <= 0) return;

    const timer = setInterval(() => {
      setCooldownTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownTime]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSendVerification = async () => {
    if (!name) {
      setAlertDialogState({
        title: "잘못된 접근입니다.",
        description: "이전 페이지로 돌아갑니다.",
        onConfirm: () => {
          navigate("/auth/login", { replace: true });
        },
      });
      return;
    }

    const { phoneNumber } = getValues();

    try {
      await sendSms({ name, phoneNumber });
      setIsVerificationSent(true);
      setSentPhoneNumber(phoneNumber);
      setRemainingTime(VERIFICATION_TIME_LIMIT);
      setCooldownTime(60);
    } catch (error) {
      // AlertDialog로 처리된 에러(429, 400 쿨타임)는 콘솔에 찍지 않음
      if (isAxiosError(error)) {
        const status = error.response?.status;
        const message = error.response?.data?.message || "";

        if (
          status === 429 ||
          (status === 400 && message.includes("초 후에 재전송할 수 있습니다"))
        ) {
          return;
        }
      }

      console.error("SMS 전송 실패:", error);
    }
  };

  const handleResendVerification = async () => {
    if (!name) return;

    const { phoneNumber } = getValues();

    try {
      await sendSms({ name, phoneNumber });
      setRemainingTime(VERIFICATION_TIME_LIMIT);
      setCooldownTime(60);
    } catch (error) {
      // AlertDialog로 처리된 에러(429, 400 쿨타임)는 콘솔에 찍지 않음
      if (isAxiosError(error)) {
        const status = error.response?.status;
        const message = error.response?.data?.message || "";

        if (
          status === 429 ||
          (status === 400 && message.includes("초 후에 재전송할 수 있습니다"))
        ) {
          return;
        }
      }

      console.error("SMS 재전송 실패:", error);
    }
  };

  const handlePhoneNumberChange = (
    value: string,
    onChange: (value: string) => void
  ) => {
    const sanitized = value.replace(/[^0-9]/g, "");
    if (sanitized.length <= 11) {
      onChange(sanitized);

      // 전화번호가 변경되고, 이미 인증번호를 발송한 상태라면 UI 리셋
      if (isVerificationSent && sanitized !== sentPhoneNumber) {
        setIsVerificationSent(false);
        setSentPhoneNumber("");
        setRemainingTime(VERIFICATION_TIME_LIMIT);
        setCooldownTime(0);
        reset({
          phoneNumber: sanitized,
          verificationCode: "",
        });
      }
    }
  };

  const onSubmit = async (data: SmsVerificationFormData) => {
    if (!isVerificationSent) {
      await handleSendVerification();
      return;
    }

    if (!name || !userId || !password || !birthDate || !data.verificationCode)
      return;

    try {
      // 1. SMS 인증 확인
      await verifySms({
        name,
        phoneNumber: data.phoneNumber,
        code: data.verificationCode,
      });

      // 2. 전화번호 저장
      signupStore.setPhoneNumber(data.phoneNumber);

      // 3. 회원가입 진행
      // 14세 미만인 경우 certNum과 입력한 보호자 정보를 전송
      // 서버에서 certNum으로 캐시된 KMC 결과를 조회하고 입력값과 비교 검증
      await signup({
        name,
        userId,
        password,
        birthDate,
        phoneNumber: data.phoneNumber,
        // 보호자 정보 (14세 미만인 경우)
        ...(parentCertNum && {
          parentCertNum,
          parentInputName,
          parentInputBirth,
        }),
      });

      // 4. 회원가입 성공 - Zustand store 정리
      // 쿠키는 백엔드에서 자동으로 설정됨
      signupStore.clearSignup();

      // 5. 승인 대기 페이지로 이동 (회원가입 직후는 항상 미승인 상태)
      navigate("/pending-approval", { replace: true });
    } catch (error) {
      console.error("회원가입 실패:", error);

      // 에러 메시지 처리
      let errorTitle = "회원가입 실패";
      let errorDescription = "회원가입 중 오류가 발생했습니다.";

      if (isAxiosError(error)) {
        const status = error.response?.status;

        if (status === 409) {
          errorTitle = "이미 가입된 회원";
          errorDescription =
            "이미 가입된 휴대전화 번호입니다.\n다른 번호로 시도해주세요.";
        } else if (status === 400) {
          errorDescription = "입력하신 정보를 다시 확인해주세요.";
        } else if (error.response?.data?.message) {
          errorDescription = error.response.data.message;
        }
      }

      setAlertDialogState({
        title: errorTitle,
        description: errorDescription,
        confirmText: "확인",
        onConfirm: () => {
          // 409 에러인 경우 전화번호 입력 화면으로 초기화
          if (isAxiosError(error) && error.response?.status === 409) {
            setIsVerificationSent(false);
            setRemainingTime(VERIFICATION_TIME_LIMIT);
            reset({ phoneNumber: "", verificationCode: "" });
            resetAlertDialogState();
          }
        },
      });
    }
  };

  return {
    // Form
    control,
    handleSubmit,
    isValid,
    trigger,
    reset,

    // State
    isVerificationSent,
    remainingTime,
    cooldownTime,
    isSending,
    isVerifying,
    isSigningUp,

    // Handlers
    handleSendVerification,
    handleResendVerification,
    handlePhoneNumberChange,
    onSubmit,

    // Helpers
    formatTime,
  };
}

